import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { StaggerContainer, StaggerItem } from '@/components/ui/motion';

interface CareGuide {
  id: number;
  title: string;
  category: string;
  description: string;
  readTime: string;
  image: string;
  alt: string;
  icon: string;
}

interface CareGuidesPreviewProps {
  guides: CareGuide[];
}

const CareGuidesPreview = ({ guides }: CareGuidesPreviewProps) => {
  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center space-y-4 mb-12">
          <h2 className="text-3xl lg:text-5xl font-headline font-semibold text-foreground">
            Expert Care Guidance
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Comprehensive resources to help you become a confident, knowledgeable pet parent.
          </p>
        </div>

        <StaggerContainer className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {guides.map((guide) => (
            <StaggerItem key={guide.id}>
              <Link
                href="/care-guides-hub"
                className="group bg-card rounded-2xl overflow-hidden shadow-soft hover:shadow-elevated transition-all duration-300 hover:-translate-y-1 block h-full"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <AppImage
                    src={guide.image}
                    alt={guide.alt}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-3">
                      <Icon name={guide.icon as any} size={24} variant="outline" className="text-primary" />
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                      {guide.category}
                    </span>
                    <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                      <Icon name="ClockIcon" size={14} variant="outline" />
                      <span>{guide.readTime}</span>
                    </div>
                  </div>

                  <h3 className="text-xl font-headline font-semibold text-foreground group-hover:text-primary transition-colors">
                    {guide.title}
                  </h3>

                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {guide.description}
                  </p>

                  <div className="pt-2 flex items-center text-primary font-medium">
                    <span className="group-hover:text-accent transition-colors">Read Guide</span>
                    <Icon
                      name="ArrowRightIcon"
                      size={16}
                      variant="outline"
                      className="ml-2 group-hover:translate-x-1 transition-transform"
                    />
                  </div>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </StaggerContainer>

        <div className="text-center mt-12">
          <Link
            href="/care-guides-hub"
            className="inline-flex items-center space-x-2 px-8 py-4 bg-secondary text-white font-headline font-semibold rounded-lg hover:bg-secondary/90 hover:shadow-elevated transition-all duration-300"
          >
            <Icon name="BookOpenIcon" size={20} variant="outline" />
            <span>Explore All Care Guides</span>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CareGuidesPreview;